
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private leftOsc: OscillatorNode | null = null;
  private rightOsc: OscillatorNode | null = null;
  private resOsc: OscillatorNode | null = null;
  private vibeOsc: OscillatorNode | null = null;
  private vibeOsc2: OscillatorNode | null = null;
  private vibeGain: GainNode | null = null;
  private noiseNode: AudioWorkletNode | ScriptProcessorNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private lfo: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;
  private mainGain: GainNode | null = null;
  private backgroundGain: GainNode | null = null;
  private analyser: AnalyserNode | null = null;

  private isStarted = false;

  constructor() {}

  async initialize() {
    if (this.isStarted) return;
    this.ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    
    this.mainGain = this.ctx.createGain();
    this.mainGain.gain.value = 1.0;
    
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 256;
    
    this.mainGain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);

    this.backgroundGain = this.ctx.createGain();
    this.backgroundGain.gain.value = 0.5;
    this.backgroundGain.connect(this.mainGain);
    
    this.setupOceanWaves();
    this.isStarted = true;
  }

  private setupOceanWaves() {
    if (!this.ctx || !this.backgroundGain) return;

    const bufferSize = 4096;
    const noise = this.ctx.createScriptProcessor(bufferSize, 1, 1);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    
    noise.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
        output[i] *= 0.11;
        b6 = white * 0.115926;
      }
    };

    this.filter = this.ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 400;

    this.lfo = this.ctx.createOscillator();
    this.lfo.frequency.value = 0.12;

    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.value = 300;

    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.filter.frequency);
    
    noise.connect(this.filter);
    this.filter.connect(this.backgroundGain);
    
    this.lfo.start();
  }

  setVibrationalState(active: boolean) {
    if (!this.ctx || !this.mainGain || !this.backgroundGain) return;
    const now = this.ctx.currentTime;

    if (active) {
      if (this.vibeOsc) return;

      // 1. SPEGNIMENTO GRADUALE DEL SOTTOFONDO (FOCUS)
      this.backgroundGain.gain.cancelScheduledValues(now);
      this.backgroundGain.gain.linearRampToValueAtTime(0, now + 1.5);

      // 2. CREAZIONE DEL "RUGGITO INTERIORE" (OBE FREQUENCY)
      this.vibeOsc = this.ctx.createOscillator();
      this.vibeOsc2 = this.ctx.createOscillator();
      this.vibeGain = this.ctx.createGain();
      const filter = this.ctx.createBiquadFilter();

      // Frequenza portante profonda
      this.vibeOsc.type = 'sawtooth';
      this.vibeOsc.frequency.setValueAtTime(40, now);
      this.vibeOsc.frequency.exponentialRampToValueAtTime(80, now + 10);

      // Frequenza di risonanza alta (il ronzio elettrico)
      this.vibeOsc2.type = 'sine';
      this.vibeOsc2.frequency.setValueAtTime(100, now);
      this.vibeOsc2.frequency.exponentialRampToValueAtTime(4, now + 10); // Battimento rallentato

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(100, now);
      filter.frequency.exponentialRampToValueAtTime(1000, now + 5);
      filter.Q.value = 15;

      this.vibeGain.gain.setValueAtTime(0, now);
      this.vibeGain.gain.linearRampToValueAtTime(0.3, now + 3);

      this.vibeOsc.connect(filter);
      this.vibeOsc2.connect(filter);
      filter.connect(this.vibeGain);
      this.vibeGain.connect(this.mainGain);

      this.vibeOsc.start();
      this.vibeOsc2.start();
    } else {
      // RIPRISTINO DEL SOTTOFONDO
      this.backgroundGain.gain.cancelScheduledValues(now);
      this.backgroundGain.gain.linearRampToValueAtTime(0.5, now + 2);

      if (this.vibeGain) {
        this.vibeGain.gain.linearRampToValueAtTime(0, now + 1.5);
        setTimeout(() => {
          this.vibeOsc?.stop();
          this.vibeOsc2?.stop();
          this.vibeOsc = null;
          this.vibeOsc2 = null;
          this.vibeGain = null;
        }, 1600);
      }
    }
  }

  playShiftCue() {
    if (!this.ctx || !this.mainGain) return;
    const now = this.ctx.currentTime;
    const duration = 1.8;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, now);
    osc.frequency.exponentialRampToValueAtTime(320, now + duration);
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(300, now);
    filter.frequency.exponentialRampToValueAtTime(1800, now + duration);
    filter.Q.value = 3;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.08, now + 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(filter).connect(gain).connect(this.mainGain);
    osc.start(now);
    osc.stop(now + duration);
  }

  setBinaural(baseFreq: number, binauralFreq: number, resonanceFreq?: number) {
    if (!this.ctx || !this.backgroundGain) return;
    const now = this.ctx.currentTime;
    const rampTime = 1.5;

    if (this.leftOsc && this.rightOsc) {
      this.leftOsc.frequency.setTargetAtTime(baseFreq, now, rampTime / 3);
      this.rightOsc.frequency.setTargetAtTime(baseFreq + binauralFreq, now, rampTime / 3);
      if (resonanceFreq && this.resOsc) {
        this.resOsc.frequency.setTargetAtTime(resonanceFreq, now, rampTime / 3);
      }
      return;
    }

    this.stopBinaural();
    const leftPanner = this.ctx.createStereoPanner();
    const rightPanner = this.ctx.createStereoPanner();
    leftPanner.pan.value = -1;
    rightPanner.pan.value = 1;
    this.leftOsc = this.ctx.createOscillator();
    this.rightOsc = this.ctx.createOscillator();
    this.leftOsc.frequency.setValueAtTime(baseFreq, now);
    this.rightOsc.frequency.setValueAtTime(baseFreq + binauralFreq, now);
    this.leftOsc.connect(leftPanner).connect(this.backgroundGain);
    this.rightOsc.connect(rightPanner).connect(this.backgroundGain);
    this.leftOsc.start();
    this.rightOsc.start();
  }

  stopBinaural() {
    this.leftOsc?.stop();
    this.rightOsc?.stop();
    this.resOsc?.stop();
    this.leftOsc = null;
    this.rightOsc = null;
    this.resOsc = null;
  }

  setVolume(val: number) {
    if (this.mainGain) {
      this.mainGain.gain.setTargetAtTime(val, this.ctx?.currentTime || 0, 0.1);
    }
  }

  duckBackground(active: boolean) {
    if (!this.backgroundGain || !this.ctx) return;
    const target = active ? 0.15 : 0.5;
    this.backgroundGain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.5);
  }

  getAudioContext() {
    return this.ctx;
  }

  getAnalyser() {
    return this.analyser;
  }

  playBuffer(buffer: AudioBuffer): AudioBufferSourceNode | null {
    if (!this.ctx || !this.mainGain) return null;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.mainGain);
    source.start();
    return source;
  }
}

export const audioEngine = new AudioEngine();
